// Etiquetas compartidas del crédito del cliente y del teléfono en el portal del vendedor.
import type { CreditPortfolioState, CreditSciSource } from './creditPortfolio';

/** Lo que se pinta donde el dato todavía no existe. Un guion, no una celda vacía. */
export const PENDING_CELL = '—';

/** Saldo y Mora de quien no tiene crédito vigente: el mismo hecho se dice con las mismas palabras. */
const NO_CREDIT_CELL = 'Sin crédito';

/**
 * Por qué una celda de crédito no trae dato. Los cuatro motivos piden cosas distintas: el de
 * permisos NO se arregla reintentando, y el de fuente no lo arregla nadie desde el piso de venta.
 */
export const CREDIT_STATE_NOTE: Record<Exclude<CreditPortfolioState, 'ready'>, string> = {
    loading: 'Consultando el crédito del cliente…',
    'no-source':
        'Este cliente no entró en la lectura de la cartera: llegó por búsqueda exacta y no es de tu tienda.',
    error: 'No se pudo leer el crédito de este cliente. Vuelve a intentar.',
    forbidden:
        'Tu rol no tiene permiso para ver el crédito de los clientes. Reintentar no lo cambia; contacta a quien administra los roles.',
};

/**
 * El SCI congelado al originar. Hoy es el único que el silo entrega: el scoring no escribe de
 * vuelta al acreditado.
 */
const SCI_AT_ORIGINATION_NOTE =
    'SCI al momento de originar su crédito más reciente. No es el SCI actual del cliente.';

const SCI_LIVE_NOTE = 'SCI actual del cliente, leído de su acreditado.';

/** De dónde salió el SCI que se pinta. Lo dice el silo, ya no se asume. */
export function sciSourceNote(source: CreditSciSource | null): string {
    return source === 'BORROWER' ? SCI_LIVE_NOTE : SCI_AT_ORIGINATION_NOTE;
}

/** Píldora del cliente con crédito vivo en otra cadena; esas cifras no se le muestran al vendedor. */
export const CREDIT_ELSEWHERE_LABEL = 'Tiene crédito en otra cadena';

const TIER_NO_CREDIT_NOTE =
    'Este cliente no tiene créditos, así que todavía no tiene un nivel de scoring.';

const TIER_NOT_RECORDED_NOTE =
    'Su crédito más reciente no dejó registrado el nivel con el que se originó.';

/** Por qué no hay nivel de scoring: sin créditos no hay originación, o el silo no lo registró. */
export function tierMissingNote(creditsCount: number): string {
    return creditsCount === 0 ? TIER_NO_CREDIT_NOTE : TIER_NOT_RECORDED_NOTE;
}

/**
 * Qué dice la mora: «Sin crédito», «Sin cuotas», «al día» o los días vencidos. Los cuatro son datos
 * distintos y ninguno es un guion.
 */
export function arrearsLabel(arrearsDays: number | null, hasActiveCredit: boolean): string {
    if (!hasActiveCredit) return NO_CREDIT_CELL;
    if (arrearsDays === null) return 'Sin cuotas';
    return arrearsDays === 0 ? 'al día' : `${String(arrearsDays)} d`;
}

/** Teléfono a 10 dígitos con separación mexicana: `55 1234 0001`. */
export function formatPhone(phone: string | null | undefined): string {
    if (!phone) return PENDING_CELL;
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) return phone;
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
}
